import React, { useContext, useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "../../../../contexts";
import CustomTag from "../../../../ui/CustomTag";
import { CircularProgress, Tab, Tabs } from "@mui/material";
import CustomList from "../../../../ui/CustomList";
import Engines from "../../../accounting/contacts/engines/Engines";
import { CustomerSupport, CustomerInfo, CustomerSite, CustomerContracts } from "./Tabs";
import CustomerHeader from "./CustomerHeader";

const DEFAULT_TAB_ROUTE = "info";

const getActiveSubscriptionLines = (customer) =>
    (customer?.subscriptions || []).filter((subscription) => subscription.status === "in_progress").flatMap((subscription) => (subscription.lines || []).map((line) => ({ ...line, subscription })));

const CustomerView = () => {
    const { customerId, tab: tabRoute } = useParams();
    const navigate = useNavigate();
    const [customer, setCustomer] = useState();
    const [isLoaded, setIsLoaded] = useState(false);
    const [refresh, setRefresh] = useState(0);
    const [hasEngineConfigs, setHasEngineConfigs] = useState(false);
    const [hasSite, setHasSite] = useState(false);
    const [hasContracts, setHasContracts] = useState(false);
    const { token } = useContext(AuthContext);
    const reference = customer?.reference;

    const fetchHasSite = useCallback(async () => {
        if (!reference) return false;
        const headers = { Authorization: `Bearer ${token}` };
        const results = await Promise.allSettled([
            axios.get(`${process.env.REACT_APP_UPA_API_HOST}webflow/${reference}/check`, { headers }),
            axios.get(`${process.env.REACT_APP_UPA_API_HOST}plesk/${reference}/check`, { headers }),
        ]);
        return results.some((result) => result.status === "fulfilled" && result.value.data?.isSite);
    }, [reference, token]);

    const fetchHasContracts = useCallback(async () => {
        if (!reference) return false;
        try {
            const response = await axios.get(`${process.env.REACT_APP_UPA_API_HOST}contracts/${reference}/check`, { headers: { Authorization: `Bearer ${token}` } });
            return Boolean(response.data?.success && response.data.hasContracts);
        } catch (error) {
            return false;
        }
    }, [reference, token]);

    const fetchHasEngineConfigs = useCallback(async () => {
        if (!reference) return false;
        try {
            const response = await axios.get(`${process.env.REACT_APP_UPA_API_HOST}engines/configs/altelis/${reference}`, { headers: { Authorization: `Bearer ${token}` } });
            return Boolean(response.data && response.data.length > 0);
        } catch (error) {
            return false;
        }
    }, [reference, token]);

    const handleRefresh = useCallback(() => setRefresh((v) => v + 1), []);
    const handleEngineChange = useCallback(() => fetchHasEngineConfigs().then(setHasEngineConfigs), [fetchHasEngineConfigs]);
    const handleSitesChange = useCallback(() => fetchHasSite().then(setHasSite), [fetchHasSite]);
    const handleContractsChange = useCallback(() => fetchHasContracts().then(setHasContracts), [fetchHasContracts]);

    const handleTabChange = useCallback(
        (event, newRoute) => {
            navigate(`/admin/inventory/customers/${customerId}/${newRoute}`, { replace: true });
        },
        [customerId, navigate]
    );

    // The view is reused across customers: drop the previous customer's data and dots
    useEffect(() => {
        setCustomer(undefined);
        setIsLoaded(false);
        setHasEngineConfigs(false);
        setHasSite(false);
        setHasContracts(false);
    }, [customerId]);

    useEffect(() => {
        let isCurrent = true;
        axios
            .get(`${process.env.REACT_APP_UPA_API_HOST}pennylane/customers/${customerId}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((response) => {
                if (isCurrent && response.data) {
                    setCustomer(response.data);
                    setIsLoaded(true);
                }
            })
            .catch((error) => console.error(error));
        return () => {
            isCurrent = false;
        };
    }, [refresh, customerId, token]);

    useEffect(() => {
        let isCurrent = true;
        const setIfCurrent = (setter) => (value) => isCurrent && setter(value);
        fetchHasEngineConfigs().then(setIfCurrent(setHasEngineConfigs));
        fetchHasSite().then(setIfCurrent(setHasSite));
        fetchHasContracts().then(setIfCurrent(setHasContracts));
        return () => {
            isCurrent = false;
        };
    }, [fetchHasEngineConfigs, fetchHasSite, fetchHasContracts]);

    const activeSubscriptionLines = useMemo(() => getActiveSubscriptionLines(customer), [customer]);
    const hasSupportTickets = Object.keys(customer?.tickets || {}).length > 0;

    const recurringCols = useMemo(
        () => [
            {
                Header: "Article / Produit",
                accessor: "label",
                icon: "article",
                Cell: ({ cell: { value } }) => <CustomTag value={value} />,
            },
            {
                Header: "Récurrence",
                accessor: "subscription",
                icon: "calendar",
                Cell: ({ value }) => {
                    const { interval, ruleType } = value;
                    return `Tous les ${interval} ${ruleType === "monthly" ? "mois" : "ans"}`;
                },
            },
            {
                Header: "Montant facturé",
                accessor: "priceBeforeTax",
                icon: "euro",
                Cell: ({ cell: { value } }) => <CustomTag className="c-badge" value={value.toFixed(2) + " €"} />,
            },
        ],
        []
    );

    // Single source of truth: URL segment, label, dot and panel of each tab. `hasDot: undefined` means the tab has no dot.
    const tabs = [
        {
            route: "info",
            label: "Infos générales",
            content: <CustomerInfo contact={customer} onRefresh={handleRefresh} />,
        },
        {
            route: "recurring",
            label: "Récurrent",
            hasDot: activeSubscriptionLines.length > 0,
            content:
                activeSubscriptionLines.length > 0 ? (
                    <>
                        <CustomList columns={recurringCols} isSelectable={false} isClickable={false} isFetchable={false} isPaginable={false} entity={activeSubscriptionLines} />
                        <div className="mensual-return-rate">
                            <span>Récurrent mensuel :</span>
                            <CustomTag value={`${customer?.mensualRateReturn?.toFixed(2) || "0.00"} €`} className="blue" />
                        </div>
                    </>
                ) : (
                    <CustomTag value="Ce client ne possède aucune facturation récurrente pour le moment." className="blue" />
                ),
        },
        {
            route: "support",
            label: "Support",
            hasDot: hasSupportTickets,
            content: <CustomerSupport customer={customer} />,
        },
        {
            route: "engine",
            label: "Moteur",
            hasDot: hasEngineConfigs,
            content: <Engines contact={customer} onEngineChange={handleEngineChange} />,
        },
        {
            route: "site",
            label: "Site",
            hasDot: hasSite,
            content: <CustomerSite contact={customer} onSitesChange={handleSitesChange} />,
        },
        {
            route: "contracts",
            label: "Contrats",
            hasDot: hasContracts,
            content: <CustomerContracts contact={customer} onContractsChange={handleContractsChange} />,
        },
    ];

    const activeTab = tabs.find((tab) => tab.route === tabRoute);
    const isKnownTabRoute = Boolean(activeTab);

    useEffect(() => {
        if (!isKnownTabRoute) {
            navigate(`/admin/inventory/customers/${customerId}/${DEFAULT_TAB_ROUTE}`, { replace: true });
        }
    }, [isKnownTabRoute, customerId, navigate]);

    return isLoaded ? (
        <>
            <div className="c-app__view">
                <CustomerHeader customer={customer} onUpdate={handleRefresh} />
                <div className="tabs-container">
                    <Tabs value={activeTab ? activeTab.route : false} onChange={handleTabChange}>
                        {tabs.map((tab) => (
                            <Tab
                                key={tab.route}
                                value={tab.route}
                                label={tab.hasDot === undefined ? <span>{tab.label}</span> : <span className={`customer-dot ${tab.hasDot ? "active" : ""}`}>{tab.label}</span>}
                            />
                        ))}
                    </Tabs>
                    {activeTab && <div role="tabpanel">{activeTab.content}</div>}
                </div>
            </div>
        </>
    ) : (
        <div className="loader-container absolute">
            <CircularProgress size={30} />
        </div>
    );
};

export default CustomerView;
